import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { CalendarClock, Loader2 } from "lucide-react";

const STATUS_LABEL = {
  disponivel: "Aguardando motorista", aceita: "Aceita", a_caminho: "A caminho", chegou_origem: "Chegou na origem",
  carga_iniciada: "Carga iniciada", em_rota: "Em rota", chegou_destino: "Chegou ao destino",
  concluida: "Concluída", cancelada: "Cancelada",
};

function Agenda({ user }) {
  const [data, setData] = useState(() => new Date().toISOString().slice(0, 10));
  const [ag, setAg] = useState(null);

  useEffect(() => {
    setAg(null);
    api.get(`/admin/agenda?data=${data}`).then((r) => setAg(r.data)).catch(() => setAg(null));
  }, [data]);

  const livres = (ag?.motoristas || []).filter((m) => m.ativo && !m.bloqueado && !m.ocupado_em);
  const ocupados = (ag?.motoristas || []).filter((m) => m.ativo && !m.bloqueado && m.ocupado_em);
  const bloqueados = (ag?.motoristas || []).filter((m) => m.bloqueado);

  return (
    <AdminShell user={user} title="Agenda e Vagas">
      <div className="px-4 mt-4 space-y-4" data-testid="admin-agenda-page">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
            <CalendarClock className="w-4 h-4 text-[#F5B301]" /> Data
          </label>
          <input data-testid="agenda-data" type="date" value={data} onChange={(e) => setData(e.target.value)}
            className="w-full mt-1.5 px-4 py-3 rounded-xl border border-slate-200 text-sm" />
        </div>

        {ag === null && <div className="flex justify-center py-14"><Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /></div>}

        {ag && (
          <>
            <div className="grid grid-cols-3 gap-2" data-testid="agenda-resumo">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 text-center">
                <p className="text-2xl font-heading font-extrabold text-navy" data-testid="agenda-capacidade">{ag.capacidade}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Vagas/horário</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 text-center">
                <p className="text-2xl font-heading font-extrabold text-emerald-600" data-testid="agenda-livres">{livres.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Disponíveis</p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3 text-center">
                <p className="text-2xl font-heading font-extrabold text-amber-600" data-testid="agenda-ocupados">{ocupados.length}</p>
                <p className="text-[9px] font-bold text-slate-400 uppercase">Ocupados</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-extrabold text-navy uppercase tracking-wide mb-2.5">Horários do dia</p>
              {ag.slots.length === 0 && <p className="text-xs text-slate-400" data-testid="agenda-sem-slots">Nenhuma corrida nesta data — todos os horários livres ({ag.capacidade} vaga(s) por horário).</p>}
              {ag.slots.map((s) => (
                <div key={s.horario} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0" data-testid={`agenda-slot-${s.horario}`}>
                  <span className="font-heading font-bold text-navy text-sm">{s.horario}</span>
                  <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${s.restantes > 0 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                    {s.restantes > 0 ? `${s.restantes} vaga(s) restante(s)` : "LOTADO"}
                  </span>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-extrabold text-navy uppercase tracking-wide mb-2.5">Motoristas nesta data</p>
              <div className="flex flex-wrap gap-1.5" data-testid="agenda-motoristas">
                {livres.map((m) => (
                  <span key={m.driver_id} className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-full" data-testid={`agenda-moto-livre-${m.driver_id}`}>🟢 {m.nome}</span>
                ))}
                {ocupados.map((m) => (
                  <span key={m.driver_id} className="text-[10px] font-bold bg-amber-100 text-amber-700 px-2.5 py-1 rounded-full" data-testid={`agenda-moto-ocupado-${m.driver_id}`}>🟡 {m.nome} ({m.ocupado_em})</span>
                ))}
                {bloqueados.map((m) => (
                  <span key={m.driver_id} className="text-[10px] font-bold bg-red-100 text-red-600 px-2.5 py-1 rounded-full" data-testid={`agenda-moto-bloq-${m.driver_id}`}>🔴 {m.nome}</span>
                ))}
                {(ag.motoristas || []).length === 0 && <p className="text-xs text-slate-400">Nenhum motorista ativo.</p>}
              </div>
              <p className="text-[10px] text-slate-400 mt-2.5">A capacidade por horário segue o nº de motoristas disponíveis, ou o valor fixo definido em Preços e Regras → "Capacidade de vagas por horário".</p>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
              <p className="text-xs font-extrabold text-navy uppercase tracking-wide mb-2.5">Corridas do dia</p>
              {ag.corridas.length === 0 && <p className="text-xs text-slate-400">Nenhuma corrida.</p>}
              {ag.corridas.map((c) => (
                <div key={c.job_id} className="py-2 border-b border-slate-50 last:border-0" data-testid={`agenda-corrida-${c.job_id}`}>
                  <p className="text-xs font-bold text-navy">{c.horario} · {c.rota}</p>
                  <p className="text-[10px] text-slate-400">{STATUS_LABEL[c.status] || c.status}{c.motorista ? ` · ${c.motorista}` : ""}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AdminShell>
  );
}

export default function AdminAgenda() {
  return <AdminGate>{(user) => <Agenda user={user} />}</AdminGate>;
}
